import { CAlert } from '@coreui/react';
import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { SuccessMessageContext } from './successMessageContextValue';

interface SuccessMessageProviderProps {
  children: ReactNode;
}

export const SuccessMessageProvider: React.FC<SuccessMessageProviderProps> = ({ children }) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const location = useLocation();

  // Auto-hide timeout duration in milliseconds (5 seconds)
  const AUTO_HIDE_DURATION = 5000;

  // Check for success message from sessionStorage on mount and on navigation
  useEffect(() => {
    const storedMessage = sessionStorage.getItem('successMessage');
    if (storedMessage) {
      sessionStorage.removeItem('successMessage');
      queueMicrotask(() => {
        setSuccessMessage(storedMessage);
        timeoutRef.current = window.setTimeout(() => {
          setSuccessMessage(null);
          timeoutRef.current = null;
        }, AUTO_HIDE_DURATION);
      });
    }
  }, [location.pathname]);

  const showSuccess = useCallback((message: string) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    setSuccessMessage(message);

    // Set new timeout to auto-hide the message
    timeoutRef.current = window.setTimeout(() => {
      setSuccessMessage(null);
      timeoutRef.current = null;
    }, AUTO_HIDE_DURATION);
  }, []);

  const hideSuccess = useCallback(() => {
    // Clear timeout when manually hiding
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setSuccessMessage(null);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(() => ({ showSuccess, hideSuccess }), [showSuccess, hideSuccess]);

  return (
    <SuccessMessageContext.Provider value={contextValue}>
      {children}
      {successMessage && (
        <div
          className="position-fixed top-0 start-50 translate-middle-x success-message-toast"
          style={{ zIndex: 9999, marginTop: '20px' }}
        >
          <CAlert color="success" dismissible onClose={hideSuccess} className="shadow">
            {successMessage}
          </CAlert>
        </div>
      )}
    </SuccessMessageContext.Provider>
  );
};
